/**
 * Media for the renderer: decoded images by URL and one <video> element per
 * main clip (a source used twice must be able to show two different moments
 * during a transition, so elements are per clip, not per URL).
 *
 * Everything loads with crossOrigin="anonymous" — a tainted canvas cannot be
 * encoded. The Spaces CDN answers with `Access-Control-Allow-Origin: *`.
 */
import { useEffect, useState } from 'react';

import { AudioClip, MainClip, VideoComposition, audioTracks, mainTrack, overlayTracks } from './composition';
import { Drawable, FrameMedia } from './render';

interface ImageEntry {
    el: HTMLImageElement;
    ready: boolean;
    failed: boolean;
    promise: Promise<void>;
}

export interface MediaCacheOptions {
    /** Draw a clip's poster while its video is not decoded yet (preview only). */
    posterFallback?: boolean;
    /** Video elements carry sound (preview). The exporter mixes audio itself. */
    withSound?: boolean;
}

export class MediaCache implements FrameMedia {
    private images = new Map<string, ImageEntry>();
    private videos = new Map<string, HTMLVideoElement>();
    private videoSrc = new Map<string, string>();
    private audios = new Map<string, HTMLAudioElement>();
    private listeners = new Set<() => void>();
    private disposed = false;

    constructor(private readonly options: MediaCacheOptions = {}) {}

    /** Called whenever a piece of media becomes drawable (or a seek lands). */
    onChange(fn: () => void): () => void {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    private emit = () => {
        if (this.disposed) return;
        this.listeners.forEach((fn) => fn());
    };

    // ─── images ─────────────────────────────────────────────────────

    loadImage(src: string): Promise<void> {
        const existing = this.images.get(src);
        if (existing) return existing.promise;
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.decoding = 'async';
        const entry: ImageEntry = { el, ready: false, failed: false, promise: Promise.resolve() };
        entry.promise = new Promise<void>((resolve) => {
            el.onload = () => {
                entry.ready = true;
                this.emit();
                resolve();
            };
            el.onerror = () => {
                entry.failed = true;
                resolve();
            };
        });
        el.src = src;
        this.images.set(src, entry);
        return entry.promise;
    }

    image(src: string): Drawable | null {
        if (!src) return null;
        const entry = this.images.get(src);
        if (!entry) {
            void this.loadImage(src);
            return null;
        }
        if (!entry.ready) return null;
        return { source: entry.el, width: entry.el.naturalWidth, height: entry.el.naturalHeight };
    }

    // ─── video ──────────────────────────────────────────────────────

    video(clip: MainClip): HTMLVideoElement {
        let el = this.videos.get(clip.id);
        if (el && this.videoSrc.get(clip.id) === clip.src) return el;
        if (el) this.disposeVideo(clip.id);
        el = document.createElement('video');
        el.crossOrigin = 'anonymous';
        el.preload = 'auto';
        el.playsInline = true;
        el.muted = !this.options.withSound;
        el.setAttribute('playsinline', '');
        ['loadeddata', 'seeked', 'canplay'].forEach((ev) => el!.addEventListener(ev, this.emit));
        el.src = clip.src;
        this.videos.set(clip.id, el);
        this.videoSrc.set(clip.id, clip.src);
        return el;
    }

    peekVideo(clipId: string): HTMLVideoElement | undefined {
        return this.videos.get(clipId);
    }

    clip(clip: MainClip): Drawable | null {
        // Colour cards have no media: the renderer fills them itself.
        if (clip.kind === 'color') return null;
        if (clip.kind === 'image') return this.image(clip.src);
        const el = this.video(clip);
        if (el.readyState >= 2 && el.videoWidth) {
            return { source: el, width: el.videoWidth, height: el.videoHeight };
        }
        if (this.options.posterFallback && clip.thumbnailUrl) return this.image(clip.thumbnailUrl);
        return null;
    }

    /** Seek a clip's element to a source time and wait until that frame is decoded. */
    seekVideo(clip: MainClip, sourceTime: number, timeoutMs = 8000): Promise<void> {
        const el = this.video(clip);
        const target = Math.max(0, Math.min(sourceTime, (el.duration || Infinity) - 0.001));
        return new Promise<void>((resolve) => {
            let done = false;
            const finish = () => {
                if (done) return;
                done = true;
                el.removeEventListener('seeked', onSeeked);
                clearTimeout(timer);
                resolve();
            };
            // "seeked" can land before the frame is drawable (readyState 1):
            // drawn then, an exported frame came out black at a cut (13 Oct
            // 2026). Wait until there is a current frame to draw.
            const whenDrawable = () => {
                if (done) return;
                if (el.readyState >= 2 && !el.seeking) { finish(); return; }
                const again = () => { el.removeEventListener('loadeddata', again); el.removeEventListener('canplay', again); whenDrawable(); };
                el.addEventListener('loadeddata', again, { once: true });
                el.addEventListener('canplay', again, { once: true });
                setTimeout(again, 60);
            };
            const onSeeked = () => whenDrawable();
            const timer = setTimeout(finish, timeoutMs);
            const ready = () => {
                if (Math.abs(el.currentTime - target) < 0.0005 && el.readyState >= 2 && !el.seeking) {
                    finish();
                    return;
                }
                el.addEventListener('seeked', onSeeked);
                el.currentTime = target;
            };
            if (el.readyState >= 1) ready();
            else el.addEventListener('loadedmetadata', ready, { once: true });
        });
    }

    // ─── audio (preview) ────────────────────────────────────────────

    audio(clip: AudioClip): HTMLAudioElement {
        let el = this.audios.get(clip.id);
        if (el && el.getAttribute('data-src') === clip.src) return el;
        if (el) {
            el.pause();
            el.removeAttribute('src');
        }
        el = new Audio();
        el.crossOrigin = 'anonymous';
        el.preload = 'auto';
        el.setAttribute('data-src', clip.src);
        el.src = clip.src;
        this.audios.set(clip.id, el);
        return el;
    }

    allAudios(): Map<string, HTMLAudioElement> {
        return this.audios;
    }

    allVideos(): Map<string, HTMLVideoElement> {
        return this.videos;
    }

    // ─── lifecycle ──────────────────────────────────────────────────

    /** Start loading everything a composition needs; drop elements it no longer uses. */
    sync(comp: VideoComposition): void {
        const clipIds = new Set<string>();
        for (const c of mainTrack(comp).clips) {
            if (c.kind === 'color') continue;
            clipIds.add(c.id);
            if (c.kind === 'image') void this.loadImage(c.src);
            else {
                this.video(c);
                if (c.thumbnailUrl && this.options.posterFallback) void this.loadImage(c.thumbnailUrl);
            }
        }
        for (const t of overlayTracks(comp)) {
            for (const l of t.layers) if (l.kind === 'image') void this.loadImage(l.src);
        }
        const audioIds = new Set<string>();
        if (this.options.withSound) {
            for (const t of audioTracks(comp)) for (const c of t.clips) {
                audioIds.add(c.id);
                this.audio(c);
            }
        }
        Array.from(this.videos.keys()).forEach((id) => { if (!clipIds.has(id)) this.disposeVideo(id); });
        Array.from(this.audios.keys()).forEach((id) => {
            if (!audioIds.has(id)) {
                const el = this.audios.get(id)!;
                el.pause();
                el.removeAttribute('src');
                this.audios.delete(id);
            }
        });
    }

    /** Resolve once every image is decoded and every video can show a frame. */
    async whenReady(comp: VideoComposition, timeoutMs = 30000): Promise<void> {
        this.sync(comp);
        const waits: Promise<void>[] = [];
        for (const c of mainTrack(comp).clips) {
            if (c.kind === 'color') continue;
            if (c.kind === 'image') waits.push(this.loadImage(c.src));
            else {
                const el = this.video(c);
                waits.push(new Promise<void>((resolve) => {
                    if (el.readyState >= 2) return resolve();
                    const done = () => resolve();
                    el.addEventListener('loadeddata', done, { once: true });
                    el.addEventListener('error', done, { once: true });
                }));
            }
        }
        for (const t of overlayTracks(comp)) for (const l of t.layers) if (l.kind === 'image') waits.push(this.loadImage(l.src));
        await Promise.race([Promise.all(waits), new Promise((r) => setTimeout(r, timeoutMs))]);
    }

    private disposeVideo(id: string) {
        const el = this.videos.get(id);
        if (!el) return;
        el.pause();
        ['loadeddata', 'seeked', 'canplay'].forEach((ev) => el.removeEventListener(ev, this.emit));
        el.removeAttribute('src');
        el.load();
        this.videos.delete(id);
        this.videoSrc.delete(id);
    }

    dispose(): void {
        this.disposed = true;
        Array.from(this.videos.keys()).forEach((id) => this.disposeVideo(id));
        this.audios.forEach((el) => {
            el.pause();
            el.removeAttribute('src');
        });
        this.audios.clear();
        this.listeners.clear();
    }
}

/** Probe a media URL's natural duration/size (for media dropped into the editor). */
export function probeVideo(src: string): Promise<{ duration: number; width: number; height: number } | null> {
    return new Promise((resolve) => {
        const el = document.createElement('video');
        el.crossOrigin = 'anonymous';
        el.preload = 'metadata';
        const timer = setTimeout(() => resolve(null), 15000);
        el.onloadedmetadata = () => {
            clearTimeout(timer);
            resolve({ duration: el.duration, width: el.videoWidth, height: el.videoHeight });
            el.removeAttribute('src');
        };
        el.onerror = () => {
            clearTimeout(timer);
            resolve(null);
        };
        el.src = src;
    });
}

export function probeAudio(src: string): Promise<number | null> {
    return new Promise((resolve) => {
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.preload = 'metadata';
        const timer = setTimeout(() => resolve(null), 15000);
        el.onloadedmetadata = () => {
            clearTimeout(timer);
            resolve(Number.isFinite(el.duration) ? el.duration : null);
        };
        el.onerror = () => {
            clearTimeout(timer);
            resolve(null);
        };
        el.src = src;
    });
}

// ─── posters for videos that arrive without a thumbnail ───────────────

const posterCache = new Map<string, Promise<string | null>>();

/** A JPEG data URL of a frame near the start of a video, cached per URL. */
export function videoPoster(src: string, at = 0.4): Promise<string | null> {
    const hit = posterCache.get(src);
    if (hit) return hit;
    const p = new Promise<string | null>((resolve) => {
        const el = document.createElement('video');
        el.crossOrigin = 'anonymous';
        el.muted = true;
        el.preload = 'auto';
        el.playsInline = true;
        const timer = setTimeout(() => resolve(null), 20000);
        el.onloadeddata = () => {
            el.currentTime = Math.min(at, (el.duration || 1) / 3);
        };
        el.onseeked = () => {
            try {
                const w = 320;
                const h = Math.max(1, Math.round((w * el.videoHeight) / Math.max(1, el.videoWidth)));
                const c = document.createElement('canvas');
                c.width = w;
                c.height = h;
                c.getContext('2d')?.drawImage(el, 0, 0, w, h);
                resolve(c.toDataURL('image/jpeg', 0.8));
            } catch {
                resolve(null);
            } finally {
                clearTimeout(timer);
                el.removeAttribute('src');
                el.load();
            }
        };
        el.onerror = () => {
            clearTimeout(timer);
            resolve(null);
        };
        el.src = src;
    });
    posterCache.set(src, p);
    return p;
}

/** The poster to show for a video: its thumbnail when it has one, else a captured frame. */
export function useVideoPoster(src: string | undefined, givenThumbnail: string | undefined, isVideo: boolean): string | undefined {
    // A "thumbnail" that is itself a video file cannot be a background image.
    const thumbnailUrl = givenThumbnail && /\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(givenThumbnail) ? undefined : givenThumbnail;
    const [poster, setPoster] = useState<string | undefined>(thumbnailUrl);
    useEffect(() => {
        if (thumbnailUrl || !isVideo || !src) {
            setPoster(thumbnailUrl);
            return undefined;
        }
        let alive = true;
        void videoPoster(src).then((p) => { if (alive && p) setPoster(p); });
        return () => { alive = false; };
    }, [src, thumbnailUrl, isVideo]);
    return poster;
}
